<template>
  <transition name="modal">
    <div v-if="visible" class="modal__mask" @click.self="handleCancel">
      <div class="modal__wrapper">
        <div
          class="modal__container"
          :class="{ 'modal__container--desktop': modalWidth }"
          :style="modalWidth ? { width: modalWidth } : {}"
        >
          <div class="modal__content">
            <!-- Header -->
            <div class="modal__header">
              <slot name="header">
                <div class="flex items-center gap-3">
                  <img v-if="icon" :src="icon" class="w-9 h-9" />
                  <h2 class="modal__title">{{ title }}</h2>
                </div>
              </slot>
              <button
                type="button"
                class="modal__close"
                @click="handleCancel"
                aria-label="Fechar"
              >
                ×
              </button>
            </div>

            <!-- Body -->
            <div class="modal__body">
              <slot></slot>
            </div>

            <!-- Footer -->
            <div v-if="!hideFooter" class="modal__footer">
              <slot name="footer">
                <div class="flex flex-col sm:flex-row gap-3 sm:justify-between">
                  <button
                    type="button"
                    class="order-2 sm:order-1 px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-white bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                    @click="handleCancel"
                    :disabled="loading"
                  >
                    {{ cancelLabel }}
                  </button>
                  <button
                    type="button"
                    class="order-1 sm:order-2 px-6 py-2.5 text-sm font-medium text-white bg-primary dark:bg-blue-600 rounded-lg hover:bg-primary-dark dark:hover:bg-blue-700 transition-colors shadow-sm"
                    @click="handleConfirm"
                    :disabled="loading"
                  >
                    {{ loading ? "Processando..." : confirmLabel }}
                  </button>
                </div>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "FormModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: null,
    },
    width: {
      type: String,
      default: "650px",
    },
    confirmLabel: {
      type: String,
      default: "Salvar",
    },
    cancelLabel: {
      type: String,
      default: "Cancelar",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    modalWidth() {
      // Não aplicar largura fixa no mobile
      if (typeof window !== "undefined" && window.innerWidth <= 768) {
        return null;
      }
      return this.width;
    },
  },
  methods: {
    handleConfirm() {
      this.$emit("confirm");
    },
    handleCancel() {
      this.$emit("update:visible", false);
      this.$emit("cancel");
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "";
  },
};
</script>

<style lang="scss" scoped>
/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal__container,
.modal-leave-active .modal__container {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal__container,
.modal-leave-to .modal__container {
  transform: scale(0.9);
}

/* Modal Mask (Overlay) */
.modal__mask {
  @apply fixed inset-0 z-50;
  @apply bg-black/50 dark:bg-black/70;
  @apply flex items-center justify-center;
  @apply p-4;
  @apply transition-colors duration-300;
  @apply overflow-y-auto;
}

/* Modal Wrapper */
.modal__wrapper {
  @apply w-full flex items-center justify-center;
  @apply min-h-full;
  @apply py-4;
}

/* Modal Container */
.modal__container {
  @apply bg-white dark:bg-slate-900;
  @apply rounded-lg shadow-xl;
  @apply max-h-[90vh];
  @apply w-full;
  @apply relative;
  @apply transition-colors duration-300;
  @apply overflow-hidden;

  /* Desktop */
  @media (min-width: 769px) {
    max-width: var(--modal-width, 650px);
  }

  /* Mobile - Following user preferences */
  @media (max-width: 768px) {
    width: 95% !important;
    max-width: 95% !important;
    @apply my-4 mx-auto;
    max-height: calc(100vh - 32px);
  }
}

/* Modal Content */
.modal__content {
  @apply flex flex-col;
  @apply h-full;
}

/* Modal Header */
.modal__header {
  @apply flex items-start justify-between;
  @apply p-6 pb-4;
  @apply border-b border-gray-200 dark:border-slate-600;
  @apply transition-colors duration-300;

  @media (max-width: 768px) {
    @apply p-4 pb-3;
  }
}

.modal__title {
  @apply text-lg font-semibold text-gray-900 dark:text-gray-100;
  @apply transition-colors duration-300;

  @media (max-width: 768px) {
    @apply text-base;
  }
}

.modal__close {
  @apply flex items-center justify-center;
  @apply w-8 h-8;
  @apply text-gray-400 dark:text-gray-500;
  @apply hover:text-gray-600 dark:hover:text-gray-300;
  @apply hover:bg-gray-100 dark:hover:bg-slate-700;
  @apply rounded-full;
  @apply text-2xl font-bold leading-none;
  @apply transition-all duration-200;
  @apply cursor-pointer;
  @apply -mt-1 -mr-1;

  &:focus {
    @apply outline-none ring-2 ring-blue-500;
  }
}

/* Modal Body */
.modal__body {
  @apply flex-1 overflow-y-auto;
  @apply p-6;

  @media (max-width: 768px) {
    @apply p-3;
  }

  /* Custom scrollbar for desktop */
  @media (min-width: 769px) {
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      @apply bg-gray-100 dark:bg-slate-900;
    }

    &::-webkit-scrollbar-thumb {
      @apply bg-gray-300 dark:bg-slate-600;
      @apply rounded-full;

      &:hover {
        @apply bg-gray-400 dark:bg-slate-500;
      }
    }
  }
}

/* Modal Footer */
.modal__footer {
  @apply p-6 pt-4;
  @apply border-t border-gray-200 dark:border-slate-600;
  @apply transition-colors duration-300;

  @media (max-width: 768px) {
    @apply p-4 pt-3;
  }
}

/* Button Styles in Footer */
.modal__footer button {
  @apply min-w-[120px];

  @media (max-width: 768px) {
    @apply flex-1 min-w-0;
  }
}

/* Ensure proper stacking on mobile */
@media (max-width: 768px) {
  .modal__mask {
    @apply p-2;
  }

  .modal__wrapper {
    @apply items-start justify-center;
  }

  .modal__footer .flex {
    @apply w-full gap-2;
  }

  .modal__footer button {
    @apply py-3 text-xs;
  }
}
</style>
